import { CheckboxBase, ChipLabel, SelectField, Typography } from 'omakase';
import { MenuItem } from 'ui';
import { MenuProps } from '@material-ui/core';
import { QuestionCheckboxResponse } from '@tock/shared/utils/questionUtils';
import { QuestionOptionalTextField } from 'forms/previsit-questions//QuestionOptionalTextField';

import {
  checkbox,
  chip,
  menuItem,
  optionLabel,
  tagContainer,
} from 'forms/previsit-questions/questionMultiSelectFieldCssDefs';
import { core } from '@tock/coretypes';
import { getOptionsFromQuestion } from 'forms/previsit-questions/questionOptions';
import { useField } from 'formik';

interface Props {
  name: string;
  question: core.TicketTypeQuestion;
}
const ITEM_HEIGHT = 36;
const ITEM_PADDING_TOP = 8;

/**
 * Sets the dropdown menu right under the select. The default behavior opens
 * a menu dialog overlaying the select underneath.
 */
const staticMenuProps: Partial<MenuProps> = {
  getContentAnchorEl: null,
  variant: 'menu',
  anchorOrigin: {
    vertical: 'bottom',
    horizontal: 'left',
  },
  PaperProps: {
    style: {
      bottom: 0,
      maxHeight: ITEM_HEIGHT * 7 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
};

export const QuestionMultiSelectField = ({ name, question }: Props) => {
  const [, { value: fieldValue }, { setValue }] = useField<QuestionCheckboxResponse>(name);
  const options = getOptionsFromQuestion(question);
  const handleSelectOption = (selectedValue: string[]) => {
    setValue({
      ...fieldValue,
      value: selectedValue,
    });
  };

  return (
    <div data-testid="pre-visit-question">
      <Typography variant="body02" data-testid="pre-visit-question-label">
        {question.text}
      </Typography>
      <SelectField
        name={`${name}.value`}
        inputProps={{
          'data-testid': 'pre-visit-question-multi-select',
          'aria-label': question.text,
        }}
        native={false}
        multiple
        value={fieldValue.value}
        onChange={(event) => {
          const {
            target: { value },
          } = event;
          handleSelectOption(value as string[]);
        }}
        renderValue={(selectedValues) => {
          const opts = selectedValues as string[];
          return (
            <div key={question.id} css={tagContainer}>
              {opts.map((opt) => (
                <ChipLabel key={opt} variant="basic" highContrast css={chip}>
                  {opt}
                </ChipLabel>
              ))}
            </div>
          );
        }}
        MenuProps={{
          ...staticMenuProps,
          MenuListProps: {
            'aria-label': question.text,
          },
        }}
      >
        {options
          .filter((option) => !!option.text)
          .map((option, index) => {
            if (option.text) {
              return (
                <MenuItem
                  value={option.text}
                  key={`${index}-${option.text}`}
                  css={menuItem}
                  onClick={(e) => e.stopPropagation()}
                >
                  <CheckboxBase
                    checked={fieldValue.value.indexOf(option.text) >= 0}
                    css={checkbox}
                    inputProps={{ 'aria-label': option.text }}
                  />
                  <Typography css={optionLabel}>{option.text}</Typography>
                </MenuItem>
              );
            }
            return null;
          })}
      </SelectField>
      <QuestionOptionalTextField name={name} question={question} value={fieldValue.value} />
    </div>
  );
};
